import React, { useRef, useState, useEffect } from 'react';
import { Bold, Italic, Underline, Link, Image as ImageIcon, Video, List, ListOrdered, MapPin, Code, Eraser } from 'lucide-react';

const SimpleRichEditor = ({ value, onChange, placeholder = "Rédigez votre article..." }) => {
  const editorRef = useRef(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imageMode, setImageMode] = useState('upload'); // 'upload' ou 'url'
  const [showHtmlMode, setShowHtmlMode] = useState(false); // Mode HTML
  const [htmlContent, setHtmlContent] = useState(''); // Contenu HTML brut
  const [mapAddress, setMapAddress] = useState({ street: '', number: '', postalCode: '', city: '' });
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState('');

  // Initialiser et mettre à jour le contenu quand value change
  useEffect(() => {
    if (editorRef.current && value !== undefined) {
      // Ne mettre à jour que si le contenu est différent pour éviter de perdre le focus
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const execCommand = (command, value = null) => {
    // Sauvegarder la sélection actuelle
    const selection = window.getSelection();
    if (!selection.rangeCount) {
      editorRef.current?.focus();
    }
    
    document.execCommand(command, false, value);
    
    // Pour les listes, s'assurer que l'éditeur a le focus et mettre à jour
    if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
      setTimeout(() => {
        editorRef.current?.focus();
        updateContent();
      }, 10);
    } else {
      editorRef.current?.focus();
      updateContent();
    }
  };

  const updateContent = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const insertLink = () => {
    if (linkUrl) {
      execCommand('createLink', linkUrl);
      setLinkUrl('');
      setShowLinkModal(false);
    }
  };

  const handleImageUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const fullImageUrl = `${process.env.REACT_APP_BACKEND_URL}${data.file_url}`;
        execCommand('insertImage', fullImageUrl);
        setShowImageModal(false);
        setImageUrl('');
      } else {
        alert('Erreur lors de l\'upload de l\'image');
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploading(false);
    }
  };

  const insertImage = () => {
    if (imageUrl) {
      execCommand('insertImage', imageUrl);
      setImageUrl('');
      setShowImageModal(false);
    }
  };

  const insertVideo = () => {
    if (videoUrl) {
      let embedUrl = videoUrl;
      // Convertir les URLs YouTube en embed
      if (videoUrl.includes('youtube.com/watch')) {
        const videoId = videoUrl.split('v=')[1]?.split('&')[0];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }
      const videoHtml = `<iframe width="560" height="315" src="${embedUrl}" frameborder="0" allowfullscreen></iframe>`;
      execCommand('insertHTML', videoHtml);
      setVideoUrl('');
      setShowVideoModal(false);
    }
  };

  // Insérer une carte OpenStreetMap via Nominatim (gratuit, sans API key)
  const insertMap = async () => {
    const { street, number, postalCode, city } = mapAddress;
    
    if (!street || !city) {
      setMapError('Veuillez remplir au moins la rue et la ville');
      return;
    }
    
    setMapLoading(true);
    setMapError('');
    
    try {
      // Construire l'adresse complète pour la recherche
      const fullAddress = `${number ? number + ' ' : ''}${street}, ${postalCode ? postalCode + ' ' : ''}${city}`;
      
      // Utiliser Nominatim (OpenStreetMap) pour géocoder l'adresse - gratuit et sans API key
      const searchUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`;
      
      const response = await fetch(searchUrl, {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'LignagesDeBruxelles/1.0'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la recherche de l\'adresse');
      }
      
      const results = await response.json();
      
      if (results.length === 0) {
        setMapError('Adresse introuvable. Vérifiez les informations saisies.');
        setMapLoading(false);
        return;
      }
      
      const { lat, lon, display_name } = results[0];
      
      // Calculer la bounding box pour le zoom (environ 500m autour du point)
      const delta = 0.005; // ~500m
      const bbox = `${parseFloat(lon) - delta},${parseFloat(lat) - delta},${parseFloat(lon) + delta},${parseFloat(lat) + delta}`;
      
      // Créer l'iframe OpenStreetMap
      const mapHtml = `
        <div style="margin: 10px 0;">
          <p style="margin-bottom: 5px; font-size: 14px;"><strong>📍 ${display_name}</strong></p>
          <iframe 
            width="100%" 
            height="300" 
            frameborder="0" 
            scrolling="no" 
            marginheight="0" 
            marginwidth="0" 
            src="https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lon}"
            style="border: 1px solid #ccc; border-radius: 8px;">
          </iframe>
          <p style="margin-top: 5px; font-size: 12px;">
            <a href="https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}" target="_blank" rel="noopener noreferrer" style="color: #1a73e8;">
              Voir sur OpenStreetMap →
            </a>
          </p>
        </div>
      `;
      
      // Fermer d'abord la modal
      setShowMapModal(false);
      
      // Attendre un petit moment pour que la modal se ferme
      setTimeout(() => {
        // S'assurer que l'éditeur a le focus et positionner le curseur à la fin
        if (editorRef.current) {
          editorRef.current.focus();
          
          // Positionner le curseur à la fin du contenu
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false); // false = collapse to end
          selection.removeAllRanges();
          selection.addRange(range);
          
          // Insérer le HTML
          document.execCommand('insertHTML', false, mapHtml);
          updateContent();
        }
      }, 100);
      
      // Réinitialiser le formulaire
      setMapAddress({ street: '', number: '', postalCode: '', city: '' });
      
    } catch (error) {
      console.error('Erreur carte:', error);
      setMapError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setMapLoading(false);
    }
  };

  // Basculer en mode HTML
  const toggleHtmlMode = () => {
    if (showHtmlMode) {
      // Quitter le mode HTML -> appliquer les changements
      if (onChange) {
        onChange(htmlContent);
      }
      // Mettre à jour l'éditeur WYSIWYG
      if (editorRef.current) {
        editorRef.current.innerHTML = htmlContent;
      }
    } else {
      // Entrer en mode HTML -> copier le contenu actuel
      setHtmlContent(editorRef.current?.innerHTML || value || '');
    }
    setShowHtmlMode(!showHtmlMode);
  };

  // Mettre à jour le HTML brut
  const handleHtmlChange = (e) => {
    setHtmlContent(e.target.value);
  };

  // Appliquer le HTML et revenir au mode WYSIWYG
  const applyHtmlAndClose = () => {
    if (onChange) {
      onChange(htmlContent);
    }
    if (editorRef.current) {
      editorRef.current.innerHTML = htmlContent;
    }
    setShowHtmlMode(false);
  };

  // Nettoyer le HTML en gardant uniquement les balises autorisées
  const cleanHtml = () => {
    const allowedTags = ['b', 'strong', 'i', 'em', 'p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a'];
    
    // Récupérer le contenu actuel
    let content = editorRef.current?.innerHTML || value || '';
    
    // Créer un élément temporaire pour parser le HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    // Fonction récursive pour nettoyer un nœud et retourner son contenu nettoyé
    const cleanNode = (node) => {
      const result = document.createDocumentFragment();
      
      for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
          // Garder les nœuds texte
          result.appendChild(child.cloneNode());
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const tagName = child.tagName.toLowerCase();
          
          if (allowedTags.includes(tagName)) {
            // Créer un nouvel élément propre avec seulement les attributs autorisés
            const cleanElement = document.createElement(tagName);
            
            // Garder href pour les liens <a>
            if (tagName === 'a' && child.hasAttribute('href')) {
              cleanElement.setAttribute('href', child.getAttribute('href'));
            }
            
            // Nettoyer récursivement les enfants et les ajouter
            const cleanedChildren = cleanNode(child);
            cleanElement.appendChild(cleanedChildren);
            
            result.appendChild(cleanElement);
          } else {
            // Balise non autorisée : garder uniquement le contenu nettoyé (pas la balise)
            const cleanedChildren = cleanNode(child);
            result.appendChild(cleanedChildren);
          }
        }
        // Ignorer les commentaires et autres types de nœuds
      }
      
      return result;
    };
    
    // Nettoyer et récupérer le résultat
    const cleanedFragment = cleanNode(tempDiv);
    
    // Créer un div temporaire pour extraire le HTML
    const resultDiv = document.createElement('div');
    resultDiv.appendChild(cleanedFragment);
    const cleanedContent = resultDiv.innerHTML;
    
    // Mettre à jour l'éditeur et le state
    if (editorRef.current) {
      editorRef.current.innerHTML = cleanedContent;
    }
    if (onChange) {
      onChange(cleanedContent);
    }
    
    // Si en mode HTML, mettre à jour aussi le textarea
    if (showHtmlMode) {
      setHtmlContent(cleanedContent);
    }
  };

  return (
    <div className="simple-rich-editor border border-gray-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 border-b border-gray-200">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="p-2 rounded hover:bg-gray-200 border border-gray-300"
          title="Gras"
        >
          <Bold className="h-4 w-4" />
        </button>
        
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="p-2 rounded hover:bg-gray-200 border border-gray-300"
          title="Italique"
        >
          <Italic className="h-4 w-4" />
        </button>
        
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="p-2 rounded hover:bg-gray-200 border border-gray-300"
          title="Souligné"
        >
          <Underline className="h-4 w-4" />
        </button>

        <div className="w-px bg-gray-300"></div>

        <button
          type="button"
          onClick={() => {
            editorRef.current?.focus();
            execCommand('insertUnorderedList');
          }}
          className="p-2 rounded hover:bg-gray-200 border border-gray-300"
          title="Liste à puces"
        >
          <List className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={() => {
            editorRef.current?.focus();
            execCommand('insertOrderedList');
          }}
          className="p-2 rounded hover:bg-gray-200 border border-gray-300"
          title="Liste numérotée"
        >
          <ListOrdered className="h-4 w-4" />
        </button>

        <div className="w-px bg-gray-300"></div>

        <button
          type="button"
          onClick={() => setShowLinkModal(true)}
          className="p-2 rounded hover:bg-gray-200 border border-gray-300"
          title="Insérer un lien"
        >
          <Link className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={() => setShowImageModal(true)}
          className="p-2 rounded hover:bg-gray-200 border border-gray-300"
          title="Insérer une image"
        >
          <ImageIcon className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={() => setShowVideoModal(true)}
          className="p-2 rounded hover:bg-gray-200 border border-gray-300"
          title="Insérer une vidéo"
        >
          <Video className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={() => setShowMapModal(true)}
          className="p-2 rounded hover:bg-gray-200 border border-gray-300"
          title="Insérer une carte"
        >
          <MapPin className="h-4 w-4" />
        </button>

        <div className="w-px bg-gray-300"></div>

        <button
          type="button"
          onClick={toggleHtmlMode}
          className={`p-2 rounded border border-gray-300 ${showHtmlMode ? 'bg-gray-800 text-white' : 'hover:bg-gray-200'}`}
          title={showHtmlMode ? "Retour au mode visuel" : "Voir le code HTML"}
        >
          <Code className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={cleanHtml}
          className="p-2 rounded hover:bg-gray-200 border border-gray-300"
          title="Nettoyer le HTML (garder uniquement le formatage de base)"
        >
          <Eraser className="h-4 w-4" />
        </button>
      </div>

      {/* Editor WYSIWYG ou HTML */}
      {showHtmlMode ? (
        <div className="relative">
          <textarea
            value={htmlContent}
            onChange={handleHtmlChange}
            className="w-full min-h-[300px] p-4 font-mono text-sm bg-gray-900 text-green-400 focus:outline-none"
            style={{ minHeight: '300px', resize: 'vertical' }}
            placeholder="<p>Votre HTML ici...</p>"
          />
          <div className="flex justify-end gap-2 p-2 bg-gray-100 border-t">
            <button
              type="button"
              onClick={() => setShowHtmlMode(false)}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={applyHtmlAndClose}
              className="px-3 py-1 text-sm text-white rounded"
              style={{ backgroundColor: 'rgb(196, 150, 96)' }}
            >
              Appliquer
            </button>
          </div>
        </div>
      ) : (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={updateContent}
          className="min-h-[300px] p-4 focus:outline-none editor-content"
          style={{ minHeight: '300px' }}
          data-placeholder={placeholder}
        />
      )}

      {/* Styles globaux pour l'éditeur */}
      <style dangerouslySetInnerHTML={{__html: `
        .editor-content:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        
        .editor-content ul {
          list-style-type: disc;
          padding-left: 2rem;
          margin: 0.5rem 0;
        }
        
        .editor-content ol {
          list-style-type: decimal;
          padding-left: 2rem;
          margin: 0.5rem 0;
        }
        
        .editor-content li {
          margin: 0.25rem 0;
        }
      `}} />

      {/* Modales */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-96">
            <h3 className="text-lg font-medium mb-4">Insérer un lien</h3>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://exemple.com"
              className="w-full p-2 border border-gray-300 rounded mb-4"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={insertLink}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Insérer
              </button>
            </div>
          </div>
        </div>
      )}

      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-96">
            <h3 className="text-lg font-medium mb-4">Insérer une image</h3>
            
            {/* Onglets */}
            <div className="flex mb-4 border-b">
              <button
                type="button"
                onClick={() => setImageMode('upload')}
                className={`px-4 py-2 border-b-2 ${
                  imageMode === 'upload' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500'
                }`}
              >
                Upload
              </button>
              <button
                type="button"
                onClick={() => setImageMode('url')}
                className={`px-4 py-2 border-b-2 ${
                  imageMode === 'url' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500'
                }`}
              >
                URL
              </button>
            </div>

            {/* Contenu selon l'onglet */}
            {imageMode === 'upload' ? (
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0])}
                  className="w-full p-2 border border-gray-300 rounded mb-4"
                  disabled={uploading}
                />
                <p className="text-sm text-gray-600 mb-4">
                  Formats supportés : JPG, PNG, GIF, WEBP (max 5MB)
                </p>
                {uploading && (
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-sm text-gray-600 mt-2">Upload en cours...</p>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://exemple.com/image.jpg"
                  className="w-full p-2 border border-gray-300 rounded mb-4"
                  autoFocus
                />
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowImageModal(false);
                  setImageUrl('');
                  setImageMode('upload');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={uploading}
              >
                Annuler
              </button>
              {imageMode === 'url' && (
                <button
                  type="button"
                  onClick={insertImage}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  disabled={!imageUrl || uploading}
                >
                  Insérer
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showVideoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-96">
            <h3 className="text-lg font-medium mb-4">Insérer une vidéo</h3>
            <input
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className="w-full p-2 border border-gray-300 rounded mb-4"
              autoFocus
            />
            <p className="text-sm text-gray-600 mb-4">
              Supporte YouTube, Vimeo et les URLs directes
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowVideoModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={insertVideo}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Insérer
              </button>
            </div>
          </div>
        </div>
      )}

      {showMapModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-[450px]">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5" style={{ color: 'rgb(196, 150, 96)' }} />
              Insérer une carte
            </h3>
            
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1">
                  <label className="block text-sm text-gray-600 mb-1">N°</label>
                  <input
                    type="text"
                    value={mapAddress.number}
                    onChange={(e) => setMapAddress(prev => ({ ...prev, number: e.target.value }))}
                    placeholder="12"
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-gray-600 mb-1">Rue *</label>
                  <input
                    type="text"
                    value={mapAddress.street}
                    onChange={(e) => setMapAddress(prev => ({ ...prev, street: e.target.value }))}
                    placeholder="Rue de la Loi"
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    autoFocus
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Code postal</label>
                  <input
                    type="text"
                    value={mapAddress.postalCode}
                    onChange={(e) => setMapAddress(prev => ({ ...prev, postalCode: e.target.value }))}
                    placeholder="1000"
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Ville *</label>
                  <input
                    type="text"
                    value={mapAddress.city}
                    onChange={(e) => setMapAddress(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Bruxelles"
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>

            {mapError && (
              <p className="text-red-500 text-sm mt-3">{mapError}</p>
            )}

            <p className="text-sm text-gray-500 mt-3">
              Utilise OpenStreetMap (gratuit, sans inscription)
            </p>

            <div className="flex gap-2 justify-end mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowMapModal(false);
                  setMapAddress({ street: '', number: '', postalCode: '', city: '' });
                  setMapError('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={mapLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={insertMap}
                disabled={mapLoading || !mapAddress.street || !mapAddress.city}
                className="px-4 py-2 text-white rounded disabled:opacity-50"
                style={{ backgroundColor: 'rgb(196, 150, 96)' }}
              >
                {mapLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Recherche...
                  </span>
                ) : (
                  'Insérer la carte'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SimpleRichEditor;